<template>
  <form class="filter" action="#" @submit.prevent="onSubmit">
    <legend class="filter__toggle">
      <Action
        :id="`${name}-toggle`"
        ref="toggle"
        :content="{
          label: t('global.filtrer'),
          tag: 'button',
          modifiers: ['black'],
          icon: 'filter',
        }"
        :aria-controls="`${name}-checkboxes`"
        :aria-expanded="isExpanded"
        :aria-selected="isExpanded"
        @click="onToggle"
      />
    </legend>

    <Transition name="g-fade">
      <fieldset
        v-show="isExpanded"
        :id="`${name}-checkboxes`"
        ref="modal"
        :aria-hidden="!isExpanded"
        :aria-labelledby="`${name}-toggle`"
        class="filter__items"
      >
        <div
          v-for="value in Object.keys(content)"
          :key="value"
          class="form-checkbox filter__item"
        >
          <input
            :id="`${name}${value}`"
            :name="name"
            :value="value"
            :checked="route.query['tag[]']?.includes(value)"
            type="checkbox"
            class="form-element"
            @change="onChange"
          />
          <label
            class="form-label"
            :for="`${name}${value}`"
            v-html="content[value]"
          >
          </label>
        </div>

        <Action
          :content="{
            label: t('global.valider'),
            tag: 'button',
            modifiers: ['btn', 'small'],
          }"
          class="filter__submit"
          @click="onSubmit"
        />
      </fieldset>
    </Transition>

    <fieldset class="filter__tags">
      <button
        v-for="tag in tags"
        :key="`active-${tag}`"
        class="filter__tag tiny-category--bordered bg-grey"
        @click="onRemove(tag)"
      >
        <span v-html="content[tag]"></span>

        <SvgSprite
          class="testimony-symbol"
          symbol="ui-close"
          size="10"
          role="img"
        />
      </button>
    </fieldset>
  </form>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import Action from '@/components/global/Action.vue'

import type { PropType } from 'vue'

type Filter = Record<string, string>

defineProps({
  content: {
    type: Object as PropType<Filter>,
    required: true,
  },
  name: {
    type: String,
    required: false,
    default: 'filter',
  },
})

const route = useRoute()
const router = useRouter()
const isExpanded = ref(false)
const toggle = ref<HTMLElement | null>(null)
const modal = ref<HTMLElement | null>(null)
const { t } = useI18n()
const tags: Set<string> = new Set([])
const tagFromQuery = route.query['tag[]']

// Add tags from query, eg: ?tag[]=actualites
if (typeof tagFromQuery === 'string') {
  tags.add(tagFromQuery)
} else if (Array.isArray(tagFromQuery)) {
  tagFromQuery.forEach(tag => typeof tag === 'string' && tags.add(tag))
}

// Close modal when clicking outside of it
onClickOutside(modal, () => (isExpanded.value = false), { ignore: [toggle] })

// Click on toggle button
const onToggle = () => (isExpanded.value = !isExpanded.value)

// Redirect to page with updated tags
const onChange = (e: Event) => {
  const { value, checked } = e.target as HTMLInputElement

  tags[checked ? 'add' : 'delete'](value)
}

// Redirect to page with updated tags
const onSubmit = () => {
  router.push({
    ...route,
    query: {
      ...route.query,
      'tag[]': tags.size ? Array.from(tags) : undefined,
      'page': undefined,
    },
  })
}

// Remove active tag from filter
const onRemove = (value: string) => {
  tags.delete(value)
  onSubmit()
}
</script>

<style lang="scss" scoped>
.filter {
  position: relative;
}

.filter__toggle {
  text-align: right;
}

.filter__items {
  position: absolute;
  z-index: 1;
  right: 0;
  width: fit-content;
  margin-top: $spacing * 0.75;
  margin-left: auto;
  padding: $spacing 0;
  text-align: right;
  background: $c-white;
  border: 0;
  border-radius: 2rem;
  box-shadow: 0 1rem 3rem 0 rgba($c-black, 20%);
}

.filter__item {
  text-align: left;

  & + & {
    margin: 0;
  }
}

.form-label {
  --form-checkbox-lh: 5rem;
  --form-checkbox-width: 2rem;

  padding-right: $spacing;
  padding-left: calc(var(--form-checkbox-width) + 3.2rem);
  transition: background 0.2s;

  &:hover,
  &:focus-visible {
    background: $c-blue-light;
  }

  &::before,
  &::after {
    left: 2rem;
  }
}

.filter__submit {
  margin: $spacing $spacing 0 auto;
  text-transform: capitalize;
}

.filter__tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: $spacing * 0.5;
  margin-top: $spacing;
  padding: 0;
  border: 0;
}

.filter__tag {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: $c-white;
  cursor: pointer;
}
</style>
